import React, {
  createContext,
  useCallback,
  useMemo,
  useReducer,
  useEffect,
  useState,
  useRef,
} from "react";
import { useHttp } from "../hooks/useHttp";
import { useAuth } from "../hooks/useAuth";
import { useMyAccount } from "../hooks/useMyAccount";
import { useNavigate } from "react-router-dom";
import { API_URLS, DEFAULT_HEADERS } from "../util/constants";
import { handleApiError } from "../util/errorHandler";

const cartReducer = (state, action) => {
  switch (action.type) {
    case "CLEAR": {
      return {
        cartItems: [],
      };
    }
    case "LOAD": {
      return {
        cartItems: action.cartArray,
      };
    }
    case "ADD": {
      const { obj, quantity } = action;
      if (state.cartItems.find((e) => e.product_id === action.obj.product_id)) {
        const newArray = state.cartItems.map((e) => {
          if (e.product_id === obj.product_id) {
            if (e.is_deleted === true) {
              return {
                ...e,
                is_deleted: false,
                quantity: parseInt(quantity),
              };
            }
            return {
              ...e,
              quantity: parseInt(e.quantity) + parseInt(quantity),
            };
          } else {
            return e;
          }
        });
        return {
          cartItems: newArray,
        };
      } else {
        const newObj = {
          ...obj,
          quantity: parseInt(quantity),
        };
        return {
          cartItems: [...state.cartItems, newObj],
        };
      }
    }
    case "DELETE": {
      const { obj } = action;
      let newArray;
      newArray = state.cartItems.map((e) => {
        if (e.product_id === obj.product_id) {
          return {
            ...e,
            is_deleted: true,
            quantity: 0,
          };
        } else {
          return e;
        }
      });
      return {
        cartItems: newArray,
      };
    }
    case "CHANGE_QUANTITY": {
      const { obj, quantity } = action;
      let newArray;
      if (parseInt(quantity) === 0) {
        newArray = state.cartItems.filter(
          (e) => e.product_id !== obj.product_id
        );
      } else {
        newArray = state.cartItems.map((e) => {
          if (e.product_id === obj.product_id) {
            return {
              ...e,
              quantity: parseInt(quantity),
            };
          } else {
            return e;
          }
        });
      }
      return {
        cartItems: newArray,
      };
    }
    default:
      return state;
  }
};

export const CartContext = createContext({});

export const CartProvider = ({ children }) => {

  const [cartState, dispatch] = useReducer(cartReducer, {
    cartItems: [],
  });
  const [cartId, setCartId] = useState(null);
  const [cashback, setCashback] = useState(false);
  const [clientCartId, setClientCartId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [isLoadingCart, setIsLoadingCart] = useState(false);
  const cartLoadedRef = useRef(false);
  const previousUserIdRef = useRef(null);
  const previousCompanyKeyRef = useRef(null);

  const { sendRequest } = useHttp();
  const { assistedPurchaseState, user, setAssistedPurchaseState, userInfo } =
    useAuth();
  const { userData, orderTargetCompanies } = useMyAccount();

  const isShoppingCenter = Boolean(userData?.is_shopping_center);
  const [selectedTargetCompanyId, setSelectedTargetCompanyId] = useState(null);

  const cartRequestCompanyId =
    isShoppingCenter && selectedTargetCompanyId != null
      ? selectedTargetCompanyId
      : null;

  const navigate = useNavigate();

  const validCart = useCallback(
    () => cartState.cartItems.filter((e) => e.is_deleted === false),
    [cartState.cartItems]
  );

  const totalValue = useCallback(() => {
    const totalValue = validCart().reduce((value, next) => {
      return value + next.quantity * next.price;
    }, 0);
    return totalValue;
  }, [validCart]);

  const selectedTargetCompany = useMemo(() => {
    if (!isShoppingCenter) return null;
    if (!Array.isArray(orderTargetCompanies) || orderTargetCompanies.length === 0) return null;
    if (selectedTargetCompanyId == null) return null;
    return (
      orderTargetCompanies.find(
        (c) => Number(c?.company_id) === Number(selectedTargetCompanyId)
      ) || null
    );
  }, [isShoppingCenter, orderTargetCompanies, selectedTargetCompanyId]);

  const selectedTargetCompanyFinancials = useMemo(() => {
    if (!isShoppingCenter) return null;
    const commercial = selectedTargetCompany?.commercial;
    if (!commercial || typeof commercial !== "object") return null;

    const toNumberOrFallback = (value, fallback) => {
      if (value === null || value === undefined || value === "") return fallback;
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : fallback;
    };

    return {
      frete: toNumberOrFallback(commercial.frete_value, 0),
      discount_percentage: toNumberOrFallback(commercial.discount_percent, 0),
      cashback_amount: toNumberOrFallback(commercial.cashback_balance, 0),
      cashback_percent: toNumberOrFallback(commercial.cashback_percent, 0),
    };
  }, [isShoppingCenter, selectedTargetCompany]);

  const orderFinalValue = useCallback(() => {
    const frete = isShoppingCenter
      ? (selectedTargetCompanyFinancials?.frete ?? 0)
      : (userInfo?.frete ?? 0);
    const cashbackAmount = cashback
      ? (isShoppingCenter
          ? (selectedTargetCompanyFinancials?.cashback_amount ?? 0)
          : (userInfo?.cashback_amount ?? 0))
      : 0;
    const discountPercentage = isShoppingCenter
      ? (selectedTargetCompanyFinancials?.discount_percentage ?? 0)
      : (userInfo?.discount_percentage ?? 0);
    return (
      totalValue() +
      frete -
      cashbackAmount -
      (totalValue() * discountPercentage / 100)
    );
  }, [
    cashback,
    isShoppingCenter,
    selectedTargetCompanyFinancials?.cashback_amount,
    selectedTargetCompanyFinancials?.discount_percentage,
    selectedTargetCompanyFinancials?.frete,
    totalValue,
    userInfo?.cashback_amount,
    userInfo?.frete,
    userInfo?.discount_percentage,
  ]);

  const assistedOrderFinalValue = useCallback(() => {
    return (
      totalValue() +
      assistedPurchaseState?.clientFrete -
      (cashback ? assistedPurchaseState?.client_cashback_amount : 0)
    );
  }, [
    assistedPurchaseState?.clientFrete,
    assistedPurchaseState?.client_cashback_amount,
    cashback,
    totalValue,
  ]);

  // cashbackValue / assistedCashbackValue eram enviados no payload de finalização (cashback_value).
  // Como não enviamos mais esse campo, removemos os callbacks para evitar warnings.

  useEffect(() => {
    if (!isShoppingCenter) {
      setSelectedTargetCompanyId(null);
      return;
    }
    if (!Array.isArray(orderTargetCompanies) || orderTargetCompanies.length === 0) {
      return;
    }
    setSelectedTargetCompanyId((prev) => {
      const stillValid =
        prev != null &&
        orderTargetCompanies.some(
          (c) => Number(c.company_id) === Number(prev)
        );
      if (stillValid) return prev;
      return orderTargetCompanies[0].company_id;
    });
  }, [isShoppingCenter, orderTargetCompanies]);

  useEffect(() => {
    // Não atualiza o banco se o carrinho está sendo carregado ou se está vazio e ainda não foi carregado
    if (isLoadingCart || (cartState.cartItems.length === 0 && !cartLoadedRef.current)) {
      return;
    }

    // Não atualiza se não houver usuário logado
    if (!user?.uid) {
      return;
    }

    // Adiciona um debounce para evitar múltiplas requisições quando vários produtos são adicionados rapidamente
    const timeoutId = setTimeout(() => {
      const updateCart = async (userId) => {
        try {
          const payload = { uid: userId, cart: cartState.cartItems };
          if (isShoppingCenter && selectedTargetCompanyId != null) {
            const cid = Number(selectedTargetCompanyId);
            if (!Number.isNaN(cid)) payload.company_id = cid;
          }
          const responseData = await sendRequest(
            API_URLS.UPDATE_CART,
            "POST",
            JSON.stringify(payload),
            DEFAULT_HEADERS.JSON
          );

          if (responseData.error) {
            throw new Error(responseData.error);
          }
        } catch (error) {
          handleApiError(error, { context: "CartContext.updateCart", showToast: false });
        }
      };
      
      // Usa user.uid do contexto ao invés de ler do localStorage diretamente
      if (assistedPurchaseState) {
        updateCart(assistedPurchaseState.clientUserId);
      }
      updateCart(user.uid);
    }, 500); // Debounce de 500ms

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartState.cartItems,
    isLoadingCart,
    user?.uid,
    isShoppingCenter,
    selectedTargetCompanyId,
  ]);

  useEffect(() => {
    const getPaymentMethods = async () => {
      try {
        if (!user?.token) return;
        const productIds = [
          ...new Set(
            validCart()
              .map((item) => (item.id ? parseInt(item.id, 10) : null))
              .filter(Boolean)
          ),
        ];
        const paymentBody = { token: user.token, product_ids: productIds };
        if (isShoppingCenter && selectedTargetCompanyId != null) {
          const cid = Number(selectedTargetCompanyId);
          if (!Number.isNaN(cid)) paymentBody.company_id = cid;
        }
        const responseData = await sendRequest(
          API_URLS.PAYMENT_METHODS,
          "POST",
          JSON.stringify(paymentBody),
          DEFAULT_HEADERS.JSON
        );
        if (responseData.success) {
          const methods = responseData.payment_methods || [];
          setPaymentMethods(methods);
          setSelectedPaymentMethod((prev) => {
            if (!prev) return prev;
            const normalizeId = (obj) => {
              const id =
                obj?.id ??
                obj?.payment_method_id ??
                obj?.paymentMethodId ??
                obj?.id_payment_method ??
                null;
              return id === null || id === undefined ? null : String(id);
            };

            const stillValid = methods.some(
              (m) => normalizeId(m) !== null && normalizeId(m) === normalizeId(prev)
            );
            return stillValid ? prev : "";
          });
        }
        if (responseData.error) {
          throw new Error(responseData.error);
        }
      } catch (error) {
        handleApiError(error, { context: "CartContext.getPaymentMethods", showToast: false });
      }
    };
    getPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartState.cartItems,
    user?.token,
    isShoppingCenter,
    selectedTargetCompanyId,
  ]);

  const loadCart = useCallback(async (userId, orderCompanyId = null) => {
    setIsLoadingCart(true);
    try {
      const body = { uid: `${userId}` };
      if (orderCompanyId != null) {
        const n =
          typeof orderCompanyId === "number"
            ? orderCompanyId
            : parseInt(String(orderCompanyId), 10);
        if (!Number.isNaN(n)) body.company_id = n;
      }
      const responseData = await sendRequest(
        API_URLS.USER_CART,
        "POST",
        JSON.stringify(body),
        DEFAULT_HEADERS.JSON
      );
      if (responseData.success) {
        setCartId(responseData.order_id);
        dispatch({
          type: "LOAD",
          cartArray: responseData.cart || [],
        });
      } else {
        throw new Error(responseData.error);
      }
    } finally {
      // Aguarda um pouco antes de setar isLoadingCart como false
      // para garantir que o dispatch seja processado primeiro
      setTimeout(() => {
        setIsLoadingCart(false);
      }, 100);
    }
  }, [sendRequest]);

  const finalizeCart = async (paymentMethodOverride = null) => {
    try {
      // Evita race condition: o React pode ainda não ter propagado `selectPaymentMethod`
      // no momento em que o usuário clica em "Finalizar compra".
      const paymentMethodToUse = paymentMethodOverride || selectPaymentMethod;
      // A API pode retornar diferentes campos para o id do método de pagamento.
      // Aceitamos os comuns para evitar travar o fluxo por diferença de shape.
      const paymentMethodId =
        paymentMethodToUse?.id ??
        paymentMethodToUse?.payment_method_id ??
        paymentMethodToUse?.paymentMethodId ??
        paymentMethodToUse?.id_payment_method ??
        null;

      if (paymentMethodId === null || paymentMethodId === undefined) {
        throw new Error("Selecione um método de pagamento para finalizar a compra.");
      }

      const paymentMethodIdForApi = (() => {
        const n =
          typeof paymentMethodId === "number"
            ? paymentMethodId
            : parseInt(String(paymentMethodId), 10);
        return Number.isNaN(n) ? paymentMethodId : n;
      })();

      const toIntOrNull = (value) => {
        const parsed = typeof value === "number" ? value : parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      };

      // Garante que o id do carrinho está carregado antes de finalizar.
      // Caso o usuário clique rápido após entrar na tela, `cartId`/`clientCartId`
      // podem ainda estar null.
      let terraCartIdInt = toIntOrNull(cartId);
      let clientCartIdInt = assistedPurchaseState ? toIntOrNull(clientCartId) : null;

      if (assistedPurchaseState) {
        if (clientCartIdInt === null) {
          clientCartIdInt = await loadClientCartId(
            assistedPurchaseState.clientUserId
          );
        }
        if (terraCartIdInt === null && user?.uid) {
          terraCartIdInt = await loadCartId(user.uid);
        }

        if (clientCartIdInt === null || terraCartIdInt === null) {
          throw new Error("Dados do carrinho não carregados. Tente novamente.");
        }
      } else {
        const scCo =
          isShoppingCenter && selectedTargetCompanyId != null
            ? Number(selectedTargetCompanyId)
            : null;
        const cartIdCompanyArg =
          scCo != null && !Number.isNaN(scCo) ? scCo : null;
        if (terraCartIdInt === null && user?.uid) {
          terraCartIdInt = await loadCartId(user.uid, cartIdCompanyArg);
        }
        if (terraCartIdInt === null) {
          throw new Error("Dados do carrinho não carregados. Tente novamente.");
        }
      }

      const resolveCompanyIdFromUserInfo = (info) => {
        if (!info || typeof info !== "object") return null;
        const raw =
          info.company_id ??
          info.id_company ??
          info.companyId ??
          info.id_empresa;
        if (raw === null || raw === undefined || raw === "") return null;
        const n =
          typeof raw === "number" ? raw : parseInt(String(raw), 10);
        return Number.isNaN(n) ? null : n;
      };

      let companyIdForOrder = assistedPurchaseState
        ? null
        : resolveCompanyIdFromUserInfo(userInfo);

      if (!assistedPurchaseState && isShoppingCenter) {
        const cid = Number(selectedTargetCompanyId);
        companyIdForOrder = Number.isNaN(cid) ? null : cid;
        if (companyIdForOrder == null) {
          companyIdForOrder = resolveCompanyIdFromUserInfo(userInfo);
        }
      }

      if (!assistedPurchaseState && companyIdForOrder == null) {
        throw new Error(
          "Não foi possível identificar a empresa do pedido (company_id). Atualize a página ou entre em contato com o suporte."
        );
      }

      const selectedCompanyTypeForOrder =
        isShoppingCenter && !assistedPurchaseState
          ? (() => {
              const raw = selectedTargetCompany?.company_type;
              if (raw === null || raw === undefined || raw === "") return null;
              const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
              return Number.isNaN(n) ? null : n;
            })()
          : null;

      const finalizePayload = assistedPurchaseState
        ? {
            payment_method_id: paymentMethodIdForApi,
            uuid: assistedPurchaseState.clientUserId,
            cart_id: clientCartIdInt,
            id_user_terra: assistedPurchaseState.terraAssistent || false,
            cart_id_user_terra: terraCartIdInt,
            use_cashback: cashback,
            company_id: assistedPurchaseState.clientCompany,
          }
        : {
            payment_method_id: paymentMethodIdForApi,
            uuid: parseInt(user.uid, 10),
            cart_id: terraCartIdInt,
            id_user_terra: null,
            use_cashback: cashback,
            ...(isShoppingCenter
              ? {
                  // Contrato backend: central de compras deve informar empresa alvo.
                  order_for_company_type: (() => {
                    if (selectedCompanyTypeForOrder == null) {
                      throw new Error(
                        "Não foi possível identificar o tipo da empresa selecionada (company_type)."
                      );
                    }
                    return selectedCompanyTypeForOrder;
                  })(),
                  order_for_company_id: companyIdForOrder,
                }
              : {}),
          };

      const responseData = await sendRequest(
        API_URLS.FINALIZE_CART,
        "POST",
        JSON.stringify(finalizePayload),
        DEFAULT_HEADERS.JSON
      );
      if (responseData.success) {
        if (assistedPurchaseState) {
          // Toast removido para evitar feedback duplicado na tela de confirmação.
        }
        // Importante: ao concluir o pedido, não podemos reutilizar o mesmo `cart_id`.
        // Limpamos o carrinho e resetamos ids/flags para que, ao voltar a comprar,
        // um novo carrinho seja carregado/criado no backend.
        dispatch({ type: "CLEAR" });
        dispatch({ type: "CLEAR" });
        setCartId(null);
        setClientCartId(null);
        cartLoadedRef.current = false;
        setAssistedPurchaseState(null);
        setCashback(false);
        navigate("/pedido-finalizado");
      } else {
        // Alguns endpoints retornam `error: true` (boolean). Garanta mensagem útil.
        const msg =
          typeof responseData.error === "string"
            ? responseData.error
            : responseData.message || "Não foi possível finalizar o pedido.";
        throw new Error(msg);
      }
    } catch (error) {
      handleApiError(error, { context: "CartContext.finalizeCart" });
      throw error;
    }
  };

  const loadClientCartId = useCallback(async (ClientUid) => {
    try {
      const responseData = await sendRequest(
        API_URLS.GET_USER_CART_ID,
        "POST",
        JSON.stringify({
          uid: parseInt(ClientUid),
        }),
        DEFAULT_HEADERS.JSON
      );
      if (responseData.success) {
        setClientCartId(responseData.id_cart);
        return responseData.id_cart;
      } else {
        throw new Error(responseData.error);
      }
    } catch (error) {
      handleApiError(error, { context: "CartContext.loadClientCartId", showToast: false });
      return null;
    }
  }, [sendRequest]);

  const loadCartId = useCallback(async (uid, orderCompanyId = null) => {
    try {
      const body = { uid: parseInt(uid, 10) };
      if (orderCompanyId != null) {
        const n =
          typeof orderCompanyId === "number"
            ? orderCompanyId
            : parseInt(String(orderCompanyId), 10);
        if (!Number.isNaN(n)) body.company_id = n;
      }
      const responseData = await sendRequest(
        API_URLS.GET_USER_CART_ID,
        "POST",
        JSON.stringify(body),
        DEFAULT_HEADERS.JSON
      );

      if (responseData.success) {
        setCartId(responseData.id_cart);
        return responseData.id_cart;
      } else {
        throw new Error(responseData.error);
      }
    } catch (error) {
      handleApiError(error, { context: "CartContext.loadCartId", showToast: false });
      return null;
    }
  }, [sendRequest]);

  const clearCart = useCallback(async () => {
    // Limpa UI imediatamente
    dispatch({ type: "CLEAR" });

    // E garante persistência no backend (mesmo se o debounce/guard pular)
    if (!user?.uid) return;
    try {
      const payload = { uid: user.uid, cart: [] };
      if (isShoppingCenter && selectedTargetCompanyId != null) {
        const cid = Number(selectedTargetCompanyId);
        if (!Number.isNaN(cid)) payload.company_id = cid;
      }
      await sendRequest(
        API_URLS.UPDATE_CART,
        "POST",
        JSON.stringify(payload),
        DEFAULT_HEADERS.JSON
      );
      if (assistedPurchaseState?.clientUserId) {
        await sendRequest(
          API_URLS.UPDATE_CART,
          "POST",
          JSON.stringify({ uid: assistedPurchaseState.clientUserId, cart: [] }),
          DEFAULT_HEADERS.JSON
        );
      }
    } catch (error) {
      handleApiError(error, { context: "CartContext.clearCart", showToast: false });
    }
  }, [
    assistedPurchaseState?.clientUserId,
    dispatch,
    sendRequest,
    user?.uid,
    isShoppingCenter,
    selectedTargetCompanyId,
  ]);

  useEffect(() => {
    if (user && user.uid) {
      const currentUserId = user.uid;
      const companyKey =
        isShoppingCenter && selectedTargetCompanyId != null
          ? String(selectedTargetCompanyId)
          : "";

      if (
        isShoppingCenter &&
        Array.isArray(orderTargetCompanies) &&
        orderTargetCompanies.length > 0 &&
        selectedTargetCompanyId == null
      ) {
        return;
      }

      const sessionKey = `${currentUserId}:${companyKey}`;
      const shouldReload =
        previousUserIdRef.current !== currentUserId ||
        previousCompanyKeyRef.current !== sessionKey;

      if (!shouldReload && cartLoadedRef.current) {
        return;
      }

      previousUserIdRef.current = currentUserId;
      previousCompanyKeyRef.current = sessionKey;
      cartLoadedRef.current = true;

      const loadCartData = async () => {
        try {
          const co = cartRequestCompanyId;
          const coArg =
            co != null
              ? typeof co === "number"
                ? co
                : parseInt(String(co), 10)
              : null;
          const companyForApi =
            coArg != null && !Number.isNaN(coArg) ? coArg : null;
          await loadCartId(currentUserId, companyForApi);
          await loadCart(currentUserId, companyForApi);
        } catch (error) {
          console.error("Erro ao carregar carrinho:", error);
          cartLoadedRef.current = false;
        }
      };
      loadCartData();
    } else {
      cartLoadedRef.current = false;
      previousUserIdRef.current = null;
      previousCompanyKeyRef.current = null;
      dispatch({ type: "CLEAR" });
      setCartId(null);
    }
  }, [
    loadCart,
    loadCartId,
    user,
    dispatch,
    setCartId,
    isShoppingCenter,
    selectedTargetCompanyId,
    orderTargetCompanies,
    cartRequestCompanyId,
  ]);


  return (
    <CartContext.Provider
      value={{
        cart: validCart(),
        cashback: cashback,
        paymentMethods: paymentMethods,
        paymentMethod: selectPaymentMethod,
        setPaymentMethod: setSelectedPaymentMethod,
        totalValue,
        dispatch,
        clearCart,
        loadCart,
        finalizeCart,
        loadCartId,
        setClientCartId,
        setCartId,
        loadClientCartId,
        setCashback,
        orderFinalValue: assistedPurchaseState
          ? assistedOrderFinalValue
          : orderFinalValue,
        isShoppingCenter,
        selectedTargetCompanyId,
        setSelectedTargetCompanyId,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
